/**
 * WAVE3 G14 — muzyka w tle (HTMLAudioElement).
 * Start po pierwszej interakcji (autoplay policy), pętla + crossfade co ~2–3 min,
 * volume 0.25, natychmiastowa reakcja na audioBus.musicOn.
 */

import { audioBus, type AudioFlags } from './AudioBus.js';

const MUSIC_BASE = '/assets/audio/music';
const DEFAULT_VOLUME = 0.25;
/** Crossfade między utworami (ms). */
const CROSSFADE_MS = 1800;
/** Co ile zmieniać utwór (ms) — środek zakresu 2–3 min. */
const ROTATE_MS = 150_000;

export const DEFAULT_MUSIC_TRACKS = [
  `${MUSIC_BASE}/music_frolic.ogg`,
  `${MUSIC_BASE}/music_fairy.ogg`,
  `${MUSIC_BASE}/music_upbeat.ogg`,
] as const;

export type MusicManagerDeps = {
  createAudio?: (url: string) => HTMLAudioElementLike;
  document?: DocumentLike | null;
  audioBus?: {
    musicOn: boolean;
    subscribe: (listener: (flags: AudioFlags) => void) => () => void;
  };
  now?: () => number;
  setTimeout?: typeof setTimeout;
  clearTimeout?: typeof clearTimeout;
  /** Czas rotacji (testy). */
  rotateMs?: number;
  crossfadeMs?: number;
  volume?: number;
  tracks?: readonly string[];
};

export type HTMLAudioElementLike = {
  src: string;
  volume: number;
  loop: boolean;
  paused: boolean;
  currentTime: number;
  play: () => Promise<void>;
  pause: () => void;
};

type DocumentLike = {
  addEventListener: (
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ) => void;
  removeEventListener: (
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ) => void;
};

function defaultCreateAudio(url: string): HTMLAudioElementLike {
  const el = new Audio(url);
  el.preload = 'auto';
  return el;
}

export class MusicManager {
  private readonly createAudio: (url: string) => HTMLAudioElementLike;
  private readonly doc: DocumentLike | null;
  private readonly bus: NonNullable<MusicManagerDeps['audioBus']>;
  private readonly setTimeoutFn: typeof setTimeout;
  private readonly clearTimeoutFn: typeof clearTimeout;
  private readonly rotateMs: number;
  private readonly crossfadeMs: number;
  private readonly volume: number;
  private readonly tracks: readonly string[];

  private current: HTMLAudioElementLike | null = null;
  private fading: HTMLAudioElementLike | null = null;
  private trackIndex = 0;
  private unlocked = false;
  private playingDesired = false;
  private inited = false;
  private unsubscribe: (() => void) | null = null;
  private rotateTimer: ReturnType<typeof setTimeout> | null = null;
  private fadeTimer: ReturnType<typeof setTimeout> | null = null;
  private unlockHandler: (() => void) | null = null;
  private crossfading = false;

  constructor(deps: MusicManagerDeps = {}) {
    this.createAudio = deps.createAudio ?? defaultCreateAudio;
    this.doc =
      deps.document !== undefined
        ? deps.document
        : typeof document !== 'undefined'
          ? document
          : null;
    this.bus = deps.audioBus ?? audioBus;
    // Safari/Chromium mogą odrzucić wyłuskaną metodę Window jako „Illegal invocation”.
    this.setTimeoutFn =
      deps.setTimeout ?? (globalThis.setTimeout.bind(globalThis) as typeof setTimeout);
    this.clearTimeoutFn =
      deps.clearTimeout ?? (globalThis.clearTimeout.bind(globalThis) as typeof clearTimeout);
    this.rotateMs = deps.rotateMs ?? ROTATE_MS;
    this.crossfadeMs = deps.crossfadeMs ?? CROSSFADE_MS;
    this.volume = deps.volume ?? DEFAULT_VOLUME;
    this.tracks = deps.tracks ?? DEFAULT_MUSIC_TRACKS;
  }

  /**
   * Podłącz subscribe + jednorazowy unlock na pointerdown.
   * Bezpieczne wywołać raz z main.ts.
   */
  init(): void {
    if (this.inited) return;
    this.inited = true;
    this.playingDesired = this.bus.musicOn;

    this.unsubscribe = this.bus.subscribe((flags) => {
      this.onFlags(flags);
    });

    if (this.doc) {
      this.unlockHandler = () => this.unlock();
      // capture + once — pierwsza interakcja użytkownika (splash/onboarding)
      this.doc.addEventListener('pointerdown', this.unlockHandler, {
        once: true,
        capture: true,
      });
      // touch/keyboard fallback (niektóre urządzenia)
      this.doc.addEventListener('keydown', this.unlockHandler, {
        once: true,
        capture: true,
      });
    }
  }

  /** Czy użytkownik odblokował audio (pierwsza interakcja). */
  get isUnlocked(): boolean {
    return this.unlocked;
  }

  /** Czy aktualnie „chcemy” grać (musicOn && unlocked). */
  get isActive(): boolean {
    return this.unlocked && this.playingDesired && this.current !== null && !this.current.paused;
  }

  get currentTrackUrl(): string | null {
    return this.current?.src ?? null;
  }

  /** Test / ręczny unlock (np. gdy pointerdown już minął). */
  unlock(): void {
    if (this.unlocked) return;
    this.unlocked = true;
    this.removeUnlockListeners();
    if (this.playingDesired) {
      this.ensurePlaying();
    }
  }

  private removeUnlockListeners(): void {
    if (!this.doc || !this.unlockHandler) return;
    try {
      this.doc.removeEventListener('pointerdown', this.unlockHandler, true);
      this.doc.removeEventListener('keydown', this.unlockHandler, true);
    } catch {
      /* ignore */
    }
    this.unlockHandler = null;
  }

  private onFlags(flags: AudioFlags): void {
    this.playingDesired = flags.musicOn;
    if (!this.unlocked) return;
    if (flags.musicOn) {
      this.ensurePlaying();
    } else {
      this.stopPlayback();
    }
  }

  private ensurePlaying(): void {
    if (!this.unlocked || !this.playingDesired) return;
    if (this.current && !this.current.paused) {
      this.current.volume = this.volume;
      this.scheduleRotate();
      return;
    }
    if (this.current && this.current.paused) {
      this.current.volume = this.volume;
      void this.safePlay(this.current);
      this.scheduleRotate();
      return;
    }
    this.startTrack(this.trackIndex, false);
  }

  private stopPlayback(): void {
    this.clearRotate();
    this.clearFade();
    this.crossfading = false;
    if (this.current) {
      try {
        this.current.pause();
      } catch {
        /* ignore */
      }
    }
    if (this.fading) {
      try {
        this.fading.pause();
      } catch {
        /* ignore */
      }
      this.fading = null;
    }
  }

  private startTrack(index: number, fromCrossfade: boolean): void {
    if (this.tracks.length === 0) return;
    const i = ((index % this.tracks.length) + this.tracks.length) % this.tracks.length;
    this.trackIndex = i;
    const url = this.tracks[i]!;
    const el = this.createAudio(url);
    el.loop = true;
    el.volume = fromCrossfade ? 0 : this.volume;
    this.current = el;
    void this.safePlay(el);
    this.scheduleRotate();
  }

  private scheduleRotate(): void {
    this.clearRotate();
    if (!this.unlocked || !this.playingDesired) return;
    if (this.tracks.length < 2) return;
    this.rotateTimer = this.setTimeoutFn(() => {
      this.rotateTimer = null;
      void this.crossfadeToNext();
    }, this.rotateMs) as ReturnType<typeof setTimeout>;
  }

  private clearRotate(): void {
    if (this.rotateTimer !== null) {
      this.clearTimeoutFn(this.rotateTimer);
      this.rotateTimer = null;
    }
  }

  private clearFade(): void {
    if (this.fadeTimer !== null) {
      this.clearTimeoutFn(this.fadeTimer);
      this.fadeTimer = null;
    }
  }

  /** Crossfade do następnego utworu na playliście. */
  async crossfadeToNext(): Promise<void> {
    if (!this.unlocked || !this.playingDesired) return;
    if (this.crossfading) return;
    if (this.tracks.length < 2) return;

    this.crossfading = true;
    const nextIndex = (this.trackIndex + 1) % this.tracks.length;
    const nextUrl = this.tracks[nextIndex]!;
    const next = this.createAudio(nextUrl);
    next.loop = true;
    next.volume = 0;
    this.fading = next;

    // Crossfade dopiero po udanym play() next — inaczej wyciszamy prev i zostajemy w ciszy.
    const started = await this.safePlay(next);
    if (!started) {
      this.crossfading = false;
      this.fading = null;
      try {
        next.pause();
      } catch {
        /* ignore */
      }
      this.scheduleRotate();
      return;
    }

    const prev = this.current;
    const steps = 12;
    const stepMs = Math.max(16, Math.floor(this.crossfadeMs / steps));
    let step = 0;

    const tick = () => {
      step += 1;
      const t = Math.min(1, step / steps);
      if (prev) prev.volume = this.volume * (1 - t);
      next.volume = this.volume * t;
      if (t >= 1) {
        if (prev) {
          try {
            prev.pause();
          } catch {
            /* ignore */
          }
        }
        this.current = next;
        this.fading = null;
        this.trackIndex = nextIndex;
        this.crossfading = false;
        this.scheduleRotate();
        return;
      }
      this.fadeTimer = this.setTimeoutFn(tick, stepMs) as ReturnType<typeof setTimeout>;
    };
    tick();
  }

  /**
   * Próba play(). Zwraca true tylko gdy play się powiodło.
   * Odrzucony Promise / wyjątek synchroniczny → false (nie połykać jako sukces).
   */
  private async safePlay(el: HTMLAudioElementLike): Promise<boolean> {
    try {
      const p = el.play();
      if (p != null && typeof (p as Promise<void>).then === 'function') {
        await p;
      }
      return true;
    } catch {
      return false;
    }
  }

  /** Reset stanu (testy). */
  dispose(): void {
    this.stopPlayback();
    this.removeUnlockListeners();
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this.current = null;
    this.fading = null;
    this.unlocked = false;
    this.inited = false;
    this.trackIndex = 0;
  }
}

/** Singleton runtime — init z main.ts. */
export const musicManager = new MusicManager();
